import {Inject, Injectable} from "@angular/core";
import {JwtHttpClientService} from "../http/jwt-http-client.service";
import {
    concatAll,
    finalize,
    firstValueFrom,
    map,
    Observable,
    of,
    reduce,
    ReplaySubject,
    share,
    Subject,
    tap
} from "rxjs";
import {AuthService} from "../auth/auth.service";
import {ConfigurationServiceConstants} from "./configuration-service-constants";
import {HttpClient, HttpResponse} from "@angular/common/http";
import {ObjectUtils} from "../utils/object-utils";
import {StringUtils} from "../utils/string-utils";

@Injectable()
export class ConfigurationService {
    /**
     * Static URL prefix for all server side calls
     * @type {string}
     */
    private static readonly URL_PREFIX = '/dataserver/configuration/';

    private contexts: {[contextName: string]: string} = {};
    private allConfigurationsObservable: Observable<any>;
    private localConfigurationsObservable: Observable<any>;
    private allConfigurations: any;
    private _configurationChanges: Subject<string> = new Subject<string>();

    /**
     *
     * @param serverHttp Server HTTP Service
     * @param localHttpClient Local HTTP Service
     * @param authService Auth Service
     * @param application Application name used for context lookup
     * @param localConfigFile Path of local config file
     * @param contextNames Context names in priority order
     */
    constructor(private serverHttp: JwtHttpClientService,
                private localHttpClient: HttpClient,
                private authService: AuthService,
                @Inject(ConfigurationServiceConstants.APPLICATION_KEY) private application: string,
                @Inject(ConfigurationServiceConstants.LOCAL_CONFIG_FILE_KEY) private localConfigFile: string,
                @Inject(ConfigurationServiceConstants.CONTEXT_NAMES_KEY) private contextNames: string[]) {
        this.contexts["application"] = application;

        // Configuration details may be specific to the current user - need to refresh after a login/logout
        this.authService.loginChanging$.subscribe(() => {
            this.allConfigurations = null;
            this.allConfigurationsObservable = null;
        });
    }

    /**
     * Returns an observable sequence of changed config specs.
     * @returns {Observable}
     */
    get configurationChanges(): Observable<string> {
        return this._configurationChanges;
    }

    public setContext(contextName: string, value: string): void {
        this.contexts[contextName] = value;
    }

    public removeContext(contextName: string): void {
        delete this.contexts[contextName];
    }

    public getString(configSpec: string, dflt?: string): Promise<string> {
        let match: string[] = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return Promise.resolve(null);
        }
        let category: string = match[1];
        let configName: string = match[2];
        return new Promise<string>((resolve, reject) => {
            this.loadConfigurations().subscribe(
                (configurations: any) => {
                    const value: string = this.findConfigurationForContext(configurations, category, configName);
                    resolve(value === undefined ? dflt : value);
                },
                (error: any) => reject(error)
            );
        })
    }

    public getBoolean(configSpec: string, dflt: boolean = false): Promise<boolean> {
        return this.getString(configSpec).then((value: string) => value === undefined ? dflt : value.toLowerCase() === 'true');
    }


    public getNumber(configSpec: string, dflt?: number): Promise<number> {
        return this.getString(configSpec).then((value: string) => {
            if (value === undefined) {
                return dflt;
            }
            let i: number = parseInt(value);
            return isNaN(i) ? undefined : i;
        });
    }

    /**
     * Saves a configuration.
     * For a "local" configuration:
     *      The configuration is persisted in local browser-based storage.
     *      An array of context names may be provided, otherwise the default context is used.
     * For a server-based configuration:
     *      The context of the saved configuration is determined by the server configuration persister and returned from the service call.
     *      This context is then used to update the locally cached configuration map.
     * @param configSpec
     * @param value
     * @param contextNames
     * @param storeInSession Indicates that the value is stored only for the session. This is only relevant for local config
     * @returns A promise of a boolean (true on success) indicating that the configuration has been saved
     */
    public saveString(configSpec: string, value: string, contextNames: string[] = [], storeInSession?: boolean): Promise<boolean> {
        let match: string[] = ConfigurationServiceConstants.configSpecPattern.exec(configSpec);
        if (!match) {
            return Promise.resolve(false);
        }
        let category: string = match[1];
        let configName: string = match[2];

        if (category === 'local') {
            const contextSpec: {[contextName: string]: string} = contextNames.reduce((contextSpec: {[contextName: string]: string}, contextName: string) => {
                if (this.contexts[contextName]) {
                    contextSpec[contextName] = this.contexts[contextName];
                }
                return contextSpec;
            }, {});
            const contextSpecKey: string = Object.keys(contextSpec).length ?
                "{" + Object.keys(contextSpec).reduce((contextSpecKey: string, contextName: string) => (contextSpecKey ? contextSpecKey + ", " : "") + contextName + " = " + contextSpec[contextName], "") + "}" :
                "default";

            // update local storage
            const storage: Storage = storeInSession ? sessionStorage : localStorage;
            let localConfigurations: any = JSON.parse(storage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
            ConfigurationServiceConstants.setConfiguration(localConfigurations, category, configName, contextSpecKey, contextSpec, value);
            storage.setItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY, JSON.stringify(localConfigurations));

            // update in-memory configs
            ConfigurationServiceConstants.setConfiguration(this.allConfigurations, category, configName, contextSpecKey, contextSpec, value);
            this._configurationChanges.next(configSpec);
            return Promise.resolve(true);
        } else {
            return firstValueFrom(this.serverHttp.put(ConfigurationService.URL_PREFIX + category + "/" + configName, value).pipe(
                map((response: HttpResponse<Object>) => {
                    let contextSpecMap: any = response.body || {};
                    for (let contextSpecKey in contextSpecMap) {
                        if (!contextSpecMap.hasOwnProperty(contextSpecKey)) {
                            continue;
                        }
                        ConfigurationServiceConstants.setConfiguration(this.allConfigurations, category, configName, contextSpecKey, contextSpecMap[contextSpecKey], value);
                        this._configurationChanges.next(configSpec);
                    }
                    return true;
                }))
            );
        }
    }

    public saveStringForUser(configSpec: string, value: string): Promise<boolean> {
        return this.saveString(configSpec, value, ['userid']);
    }

    public saveBoolean(configSpec: string, value: boolean, contextNames?: string[]): Promise<boolean> {
        return this.saveString(configSpec, value.toString(), contextNames);
    }

    public saveBooleanForUser(configSpec: string, value: boolean): Promise<boolean> {
        return this.saveString(configSpec, value.toString(), ['userid']);
    }

    public saveNumber(configSpec: string, value: number, contextNames?: string[]): Promise<boolean> {
        return this.saveString(configSpec, value.toString(), contextNames);
    }

    public saveNumberForBoolean(configSpec: string, value: number): Promise<boolean> {
        return this.saveString(configSpec, value.toString(), ['userid']);
    }

    /**
     * Returns an observable for the configuration data.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    private loadConfigurations(): Observable<any> {
        // Only allow a single concurrent request
        return this.allConfigurationsObservable ?
            this.allConfigurationsObservable :
            this.authService.isLoggedIn() ?
                this.loadAllConfigurations() :
                this.loadLocalConfigurations();
    }

    /**
     * Returns an observable for the merged configuration data from the server and the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    private loadAllConfigurations(): Observable<any> {
        // Only allow a single concurrent request
        if (this.allConfigurationsObservable) {
            return this.allConfigurationsObservable;
        }

        // Configurations are cached
        if (this.allConfigurations) {
            return of(this.allConfigurations);
        }

        let serverObservable: Observable<any> = this.serverHttp.get(ConfigurationService.URL_PREFIX).pipe(
            map((response: HttpResponse<Object>) => response.body)
        );

        let localObservable: Observable<any>;
        if (this.localConfigFile) {
            localObservable = this.localHttpClient.get(this.localConfigFile, {
                observe: "response",
                responseType: "text"
            }).pipe(
                map((response: HttpResponse<string>) => JSON.parse(StringUtils.stripJsonComments(response.body)))
            );
        } else {
            localObservable = of({});
        }

        return this.allConfigurationsObservable = of(serverObservable, localObservable).pipe(
            concatAll(),
            reduce((combinedConfigurations: any, configurations: any) => {
                if (combinedConfigurations == null) {
                    // server configurations
                    return configurations;
                } else {
                    // local 'static' configurations
                    for (let configName in configurations) {
                        if (!configurations.hasOwnProperty(configName)) {
                            continue;
                        }
                        ConfigurationServiceConstants.setConfiguration(combinedConfigurations, "local", configName, "default", {"": ""}, configurations[configName]);
                    }

                    // local storage (saved) configurations
                    let localConfigurations: any = JSON.parse(localStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                    ObjectUtils.deepAssign(combinedConfigurations, localConfigurations);

                    // local session storage (saved) configurations
                    localConfigurations = JSON.parse(sessionStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                    ObjectUtils.deepAssign(combinedConfigurations, localConfigurations);

                    return combinedConfigurations;
                }
            }, null),
            tap((configurations: any) => this.allConfigurations = configurations),
            finalize(() => this.allConfigurationsObservable = undefined),
            share()
        );
    }

    /**
     * Returns an observable for the configuration data from the local configuration file.
     * Multiple concurrent calls to this method will return the same observable, so that the data will not be fetched multiple times.
     * @returns {any}
     */
    private loadLocalConfigurations(): Observable<any> {
        // Only allow a single concurrent request
        if (this.localConfigurationsObservable) {
            return this.localConfigurationsObservable;
        }
        if (!this.localConfigFile) {
            return of({});
        }

        return this.localConfigurationsObservable = this.localHttpClient.get(this.localConfigFile, {
            observe: "response",
            responseType: "text"
        }).pipe(
            map((response: HttpResponse<string>) => {
                let json: any = JSON.parse(StringUtils.stripJsonComments(response.body));
                let configurations: any = {};
                for (let configName in json) {
                    if (!json.hasOwnProperty(configName)) {
                        continue;
                    }
                    ConfigurationServiceConstants.setConfiguration(configurations, "local", configName, "default", {"": ""}, json[configName]);
                }

                // local storage (saved) configurations
                let localConfigurations: any = JSON.parse(localStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                ObjectUtils.deepAssign(configurations, localConfigurations);

                // local session storage (saved) configurations
                localConfigurations = JSON.parse(sessionStorage.getItem(ConfigurationServiceConstants.LOCAL_STORAGE_KEY)) || {};
                ObjectUtils.deepAssign(configurations, localConfigurations);

                return configurations;
            }),
            share({
                connector: () => new ReplaySubject(1),
                resetOnError: false,
                resetOnComplete: false,
                resetOnRefCountZero: false
            })
        );
    }

    /**
     * Finds the configured value in the given configurations map for the given category and configName, which best matches the current context.
     * @param configurations configurations map loaded from the server.
     * @param category The category name for the config.
     * @param configName The config name for which a value is required.
     * @return {string} The best match config value.
     */
    private findConfigurationForContext(configurations: any, category: string, configName: string): string {
        if (!configurations.hasOwnProperty(category)) {
            return undefined;
        }
        let configurationMap: {[configName: string]: {[contextSpecKey: string]: string}} = configurations[category];
        if (!configurationMap.hasOwnProperty(configName)) {
            return undefined;
        }
        let contextSpecMap: {[contextSpecKey: string]: string} = configurationMap[configName];
        let contextSpecs: {[contextSpecKey: string]: {[contextName: string]: string}} = configurations['contextSpecs'];
        let maxScore: number = 0;
        let config: string = contextSpecMap["default"];
        for (let contextSpecKey in contextSpecMap) {
            let score: number = this.scoreContextSpec(contextSpecs[contextSpecKey]);
            if (score > maxScore) {
                maxScore = score;
                config = contextSpecMap[contextSpecKey];
            }
        }
        return config;
    }

    /**
     * Assigns a score to the given context spec as matched against the current context.
     * A higher number signifies a more specific match.
     * @param contextSpec
     * @return {number} the score
     */
    private scoreContextSpec(contextSpec: {[contextName: string]: string}): number {
        // A context spec is not a match if it specifies a context name that has not been set to the same value in the current context
        return Object.keys(contextSpec).every((contextName: string) => !contextSpec[contextName] || this.contexts[contextName] === contextSpec[contextName]) ?
            // Compare each context spec value to the corresponding value in the current context, using the defined priority order.
            // If the current context has a value for a context name, but the context spec does not, then the sub-score is 0 for the context name, otherwise 1.
            this.contextNames
                .map((contextName: string) => this.contexts[contextName] && !contextSpec[contextName] ? 0 : 1)
                .reduce((score: number, subScore: number) => (score << 1) + subScore, 0) :
            0;
    }
}
